import apiClient from './apiClient';

export const deleteDailyTrendlines = async ({ symbol } = {}) => {
  const params = {};
  if (symbol) {
    params.symbol = symbol;
  }

  const response = await apiClient.delete('/admin/trendlines', {
    params: Object.keys(params).length ? params : undefined,
  });

  return response.data;
};

export const triggerTrendlineDetection = async () => {
  const response = await apiClient.post('/admin/trendlines/detect');
  return response.data;
};

export const deleteWeeklyTrendlines = async ({ symbol } = {}) => {
  const params = {};
  if (symbol) {
    params.symbol = symbol;
  }

  const response = await apiClient.delete('/admin/trendlines/weekly', {
    params: Object.keys(params).length ? params : undefined,
  });

  return response.data;
};

export const triggerWeeklyTrendlineDetection = async () => {
  const response = await apiClient.post('/admin/trendlines/weekly/detect');
  return response.data;
};

export const backfillDailyCandles = async ({ symbol, years }) => {
  const response = await apiClient.post('/admin/backfill/daily-candles', {
    symbol,
    years,
  });
  return response.data;
};

export const backfillWeeklyCandles = async ({ symbol, years }) => {
  const response = await apiClient.post('/admin/backfill/weekly-candles', {
    symbol,
    years,
  });
  return response.data;
};

export const backfillSwings = async ({ symbol, years, timeframes }) => {
  const response = await apiClient.post('/admin/backfill/swings', {
    symbol,
    years,
    timeframes,
  });
  return response.data;
};

export const backfillHourlyData = async ({ symbol, hours }) => {
  const response = await apiClient.post('/admin/backfill/hourly', {
    symbol,
    hours,
  });
  return response.data;
};

export const backfillIndicatorEvents = async ({ years, symbol, symbols }) => {
  const payload = { years };
  if (Array.isArray(symbols) && symbols.length) {
    payload.symbols = symbols;
  } else if (symbol) {
    payload.symbol = symbol;
  }

  const response = await apiClient.post('/admin/backfill/events', payload);
  return response.data;
};

export const clearEvents = async ({ symbol, symbols }) => {
  const payload = {};
  if (Array.isArray(symbols) && symbols.length) {
    payload.symbols = symbols;
  } else if (symbol) {
    payload.symbol = symbol;
  }

  const response = await apiClient.post('/admin/events/clear', payload);
  return response.data;
};

export const clearCustomEvents = async ({ symbol, symbols, accountId } = {}) => {
  const payload = {};
  if (Array.isArray(symbols) && symbols.length) {
    payload.symbols = symbols;
  } else if (symbol) {
    payload.symbol = symbol;
  }
  if (accountId) {
    payload.accountId = accountId;
  }

  const response = await apiClient.post('/admin/events/clear-custom-events', payload);
  return response.data;
};

export const runCustomEventEvaluation = async ({ symbol, symbols, accountId, lookbackDays } = {}) => {
  const payload = {};
  if (Array.isArray(symbols) && symbols.length) {
    payload.symbols = symbols;
  } else if (symbol) {
    payload.symbol = symbol;
  }
  if (accountId) {
    payload.accountId = accountId;
  }
  if (typeof lookbackDays !== 'undefined' && lookbackDays !== null && lookbackDays !== '') {
    payload.lookbackDays = lookbackDays;
  }

  const response = await apiClient.post('/admin/custom-events/evaluate', payload);
  return response.data;
};

export const getSystemSettings = async ({ prefix } = {}) => {
  const params = {};
  if (prefix) {
    params.prefix = prefix;
  }

  const response = await apiClient.get('/admin/system-settings', {
    params: Object.keys(params).length ? params : undefined,
  });

  return response.data;
};

export const updateSystemSetting = async (settingKey, value) => {
  const response = await apiClient.put(`/admin/system-settings/${encodeURIComponent(settingKey)}`, {
    value,
  });
  return response.data;
};

export const deleteSystemSetting = async (settingKey) => {
  const response = await apiClient.delete(`/admin/system-settings/${encodeURIComponent(settingKey)}`);
  return response.data;
};

export const getAdminSymbols = async () => {
  const response = await apiClient.get('/admin/symbols');
  return response.data;
};

export const createAdminSymbol = async ({ symbol, name, isActive, supportedTimeframes }) => {
  const payload = {
    symbol,
    name,
    isActive: typeof isActive === 'boolean' ? String(isActive) : isActive,
  };

  if (typeof supportedTimeframes !== 'undefined') {
    payload.supportedTimeframes = supportedTimeframes;
  }

  const response = await apiClient.post('/admin/symbols', payload);
  return response.data;
};

export const updateAdminSymbol = async (symbol, updates = {}) => {
  const payload = {};

  if (Object.prototype.hasOwnProperty.call(updates, 'isActive')) {
    payload.isActive =
      typeof updates.isActive === 'boolean' ? String(updates.isActive) : updates.isActive;
  }

  if (Object.prototype.hasOwnProperty.call(updates, 'supportedTimeframes')) {
    payload.supportedTimeframes = updates.supportedTimeframes;
  }

  if (Object.keys(payload).length === 0) {
    throw new Error('No symbol updates provided.');
  }

  // Symbols with "/" (BTC/GBP) break API Gateway path params — %2F gets decoded
  // into an extra path segment so /:symbol 404s. Use the query-param route for
  // pair symbols (mirrors getSymbolTags). Without this, no pair symbol could be
  // toggled active/edited from the admin UI.
  const response = symbol.includes('/')
    ? await apiClient.patch('/admin/symbols', payload, { params: { symbol } })
    : await apiClient.patch(`/admin/symbols/${encodeURIComponent(symbol)}`, payload);
  return response.data;
};

// Symbol tag API functions
export const getSymbolTags = async (symbol) => {
  // Symbols with "/" break API Gateway path params (%2F gets decoded)
  // Use query-param route as fallback for pair symbols
  const response = symbol.includes('/')
    ? await apiClient.get('/admin/symbol-tags', { params: { symbol } })
    : await apiClient.get(`/admin/symbols/${encodeURIComponent(symbol)}/tags`);
  return response.data;
};

export const addSymbolTag = async (symbol, name, tag) => {
  const response = await apiClient.post('/admin/symbols/tags', { symbol, name, tag });
  return response.data;
};

export const removeSymbolTag = async (symbol, tag) => {
  const response = await apiClient.delete('/admin/symbols/tags', { data: { symbol, tag } });
  return response.data;
};

export const getAllTags = async () => {
  const response = await apiClient.get('/admin/tags');
  return response.data;
};

// Analytics API functions
export const getAnalyticsOverview = async ({ from, to } = {}) => {
  const params = {};
  if (from) params.from = from;
  if (to) params.to = to;

  const response = await apiClient.get('/admin/analytics/overview', {
    params: Object.keys(params).length ? params : undefined,
  });
  return response.data;
};

export const getAnalyticsActiveUsers = async ({ from, to } = {}) => {
  const params = {};
  if (from) params.from = from;
  if (to) params.to = to;

  const response = await apiClient.get('/admin/analytics/active-users', {
    params: Object.keys(params).length ? params : undefined,
  });
  return response.data;
};

export const getAnalyticsPopularEndpoints = async ({ from, to, limit } = {}) => {
  const params = {};
  if (from) params.from = from;
  if (to) params.to = to;
  if (limit) params.limit = limit;

  const response = await apiClient.get('/admin/analytics/popular-endpoints', {
    params: Object.keys(params).length ? params : undefined,
  });
  return response.data;
};

export const getAnalyticsPopularSymbols = async ({ from, to, limit } = {}) => {
  const params = {};
  if (from) params.from = from;
  if (to) params.to = to;
  if (limit) params.limit = limit;

  const response = await apiClient.get('/admin/analytics/popular-symbols', {
    params: Object.keys(params).length ? params : undefined,
  });
  return response.data;
};

export const getAnalyticsDeviceSplit = async ({ from, to } = {}) => {
  const params = {};
  if (from) params.from = from;
  if (to) params.to = to;

  const response = await apiClient.get('/admin/analytics/device-split', {
    params: Object.keys(params).length ? params : undefined,
  });
  return response.data;
};

export const getAnalyticsAuthMethods = async ({ from, to } = {}) => {
  const params = {};
  if (from) params.from = from;
  if (to) params.to = to;

  const response = await apiClient.get('/admin/analytics/auth-methods', {
    params: Object.keys(params).length ? params : undefined,
  });
  return response.data;
};

export const getAnalyticsGeo = async ({ from, to } = {}) => {
  const params = {};
  if (from) params.from = from;
  if (to) params.to = to;

  const response = await apiClient.get('/admin/analytics/geo', {
    params: Object.keys(params).length ? params : undefined,
  });
  return response.data;
};

export const getAnalyticsPeakHours = async ({ from, to } = {}) => {
  const params = {};
  if (from) params.from = from;
  if (to) params.to = to;

  const response = await apiClient.get('/admin/analytics/peak-hours', {
    params: Object.keys(params).length ? params : undefined,
  });
  return response.data;
};

export const getAnalyticsRetention = async ({ from, to } = {}) => {
  const params = {};
  if (from) params.from = from;
  if (to) params.to = to;

  const response = await apiClient.get('/admin/analytics/retention', {
    params: Object.keys(params).length ? params : undefined,
  });
  return response.data;
};

export const getAnalyticsUsers = async ({ from, to } = {}) => {
  const params = {};
  if (from) params.from = from;
  if (to) params.to = to;

  const response = await apiClient.get('/admin/analytics/users', {
    params: Object.keys(params).length ? params : undefined,
  });
  return response.data;
};

export const getAnalyticsUserDetails = async (accountId, { from, to } = {}) => {
  const params = {};
  if (from) params.from = from;
  if (to) params.to = to;

  const response = await apiClient.get(`/admin/analytics/users/${encodeURIComponent(accountId)}`, {
    params: Object.keys(params).length ? params : undefined,
  });
  return response.data;
};

export const adminSetPassword = async (accountId, password) => {
  const response = await apiClient.put(`/admin/users/${encodeURIComponent(accountId)}/password`, { password });
  return response.data;
};

// Account management API functions
export const getAdminAccounts = async () => {
  const response = await apiClient.get('/admin/accounts');
  return response.data;
};

export const activateAccount = async (accountId) => {
  const response = await apiClient.post(`/admin/accounts/${encodeURIComponent(accountId)}/activate`);
  return response.data;
};

// Cycle-detection admin: clears CycleStateTable rows AND deletes the scope's
// EventsTable rows for (symbol, timeframe[, side]), so the next scheduled cron
// run re-backfills both from the batch detector. The EventsTable half is why
// this needs an acknowledgement — a rebuilt row cannot carry `revoked`.
// Defined in serverless-portfolio-tracker#387. The backend expects canonical
// short-form timeframes ('1w'|'1d'|'1h') — frontend long-forms
// ('weekly'|'daily'|'hourly') must be mapped at the call site.
export const recomputeCycles = async ({
  symbol, timeframe, side, acknowledgeLedgerBypass,
} = {}) => {
  const body = { symbol, timeframe };
  if (side) {
    body.side = side;
  }
  // (DWLF-104) The backend 409s without this. Passed through from the caller
  // rather than defaulted here on purpose: defaulting it true would satisfy the
  // guard on every call and defeat it. Only a UI that has shown the admin what
  // is being undone should set it.
  if (acknowledgeLedgerBypass === true) {
    body.acknowledgeLedgerBypass = true;
  }
  const response = await apiClient.post('/admin/cycles/recompute', body);
  return response.data;
};

// Measure forward-confidence for a (symbol, timeframe[, side]) tuple
// against either the supplied settings or the current SystemSettings.
// Returns the proportion of historical confirmed pivots followed by
// a price move of `outcomeMovePct` within `outcomeWindowBars`. Used
// by the Tune panel to display a measured accuracy number for the
// current tune (instead of just "preview looks reasonable") and by
// the auto-calibrators (Tasks #22/#23) as the fitness function.
export const cyclesForwardConfidence = async ({
  symbol,
  timeframe,
  side,
  settings,
  outcomeMovePct,
  outcomeWindowBars,
  minSampleSize,
} = {}) => {
  const body = { symbol, timeframe };
  if (side) {
    body.side = side;
  }
  if (settings) {
    body.settings = settings;
  }
  if (Number.isFinite(outcomeMovePct)) {
    body.outcomeMovePct = outcomeMovePct;
  }
  if (Number.isFinite(outcomeWindowBars)) {
    body.outcomeWindowBars = outcomeWindowBars;
  }
  if (Number.isFinite(minSampleSize)) {
    body.minSampleSize = minSampleSize;
  }
  const response = await apiClient.post('/admin/cycles/forward-confidence', body);
  return response.data;
};
